import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, BookOpen, Video, FileText, Mail, Ticket, Edit2, Trash2, MessageSquare, CreditCard, Lightbulb, ArrowRight, Pencil, Send, X, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEOHead } from "@/components/SEOHead";
import { newArticleTiles } from "@/data/supportArticles";
import { getArticleSuggestions } from "@/lib/supportSearch";

export default function Support() {
  const { toast } = useToast();
  const { user, subscription } = useAuth();
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [viewTicketDialogOpen, setViewTicketDialogOpen] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // Form states
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedTicketQuery, setDebouncedTicketQuery] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedTicketQuery(`${subject} ${description}`);
    }, 200);
    return () => clearTimeout(handle);
  }, [subject, description]);

  const articleSuggestions = useMemo(
    () => getArticleSuggestions(debouncedTicketQuery, 3),
    [debouncedTicketQuery]
  );

  const isLoggedIn = !!user;
  const hasActiveSubscription = subscription?.status === 'active';
  const canSubmitTickets = isLoggedIn && hasActiveSubscription;

  useEffect(() => {
    if (canSubmitTickets) {
      fetchTickets();
    }
  }, [canSubmitTickets]);

  const fetchTickets = async () => {
    const { data } = await supabase
      .from("support_tickets")
      .select("*")
      .eq("user_id", user?.id)
      .order("created_at", { ascending: false });

    const ticketsData = data || [];

    // Determine which tickets have a pending support reply (latest comment is admin)
    if (ticketsData.length > 0) {
      const ids = ticketsData.map((t) => t.id);
      const { data: commentsData } = await supabase
        .from("ticket_comments")
        .select("ticket_id, is_admin_reply, created_at")
        .in("ticket_id", ids)
        .order("created_at", { ascending: false });

      const latestByTicket = new Map<string, { is_admin_reply: boolean; created_at: string }>();
      (commentsData || []).forEach((c: any) => {
        if (!latestByTicket.has(c.ticket_id)) {
          latestByTicket.set(c.ticket_id, c);
        }
      });

      ticketsData.forEach((t: any) => {
        const latest = latestByTicket.get(t.id);
        t.has_pending_admin_reply = !!latest?.is_admin_reply;
      });
    }

    setTickets(ticketsData);
  };

  const fetchComments = async (ticketId: string) => {
    const { data, error } = await supabase
      .from("ticket_comments")
      .select("*")
      .eq("ticket_id", ticketId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Failed to load comments", error);
    }
    setComments(data || []);
  };

  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !canSubmitTickets) return;

    setIsSubmitting(true);

    const { error } = await supabase
      .from("support_tickets")
      .insert({
        user_id: user.id,
        subject,
        category,
        description,
        status: 'open',
        priority: 'medium'
      });

    setIsSubmitting(false);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Ticket submitted!",
        description: "Our support team will respond soon.",
      });
      setSubject("");
      setCategory("");
      setDescription("");
      setTicketDialogOpen(false);
      fetchTickets();
    }
  };

  const handleUpdateTicket = async (ticketId: string, updates: any) => {
    const { error } = await supabase
      .from("support_tickets")
      .update(updates)
      .eq("id", ticketId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Ticket updated",
        description: "Your ticket has been updated successfully.",
      });
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, ...updates });
      }
    }
  };

  const handleDeleteTicket = async (ticketId: string) => {
    const { error } = await supabase
      .from("support_tickets")
      .delete()
      .eq("id", ticketId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Ticket deleted",
        description: "Your ticket has been deleted successfully.",
      });
      fetchTickets();
      setViewTicketDialogOpen(false);
    }
  };

  const handleViewTicket = async (ticket: any) => {
    setSelectedTicket(ticket);
    setIsEditingHeader(false);
    setEditSubject(ticket.subject || "");
    setEditDescription(ticket.description || "");
    await fetchComments(ticket.id);
    setViewTicketDialogOpen(true);
  };

  const handleSaveHeader = async () => {
    if (!selectedTicket) return;
    if (!editSubject.trim()) {
      toast({ title: "Subject required", variant: "destructive" });
      return;
    }
    await handleUpdateTicket(selectedTicket.id, {
      subject: editSubject.trim(),
      description: editDescription,
    });
    setIsEditingHeader(false);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;

    const { error } = await supabase
      .from("ticket_comments")
      .insert({
        ticket_id: selectedTicket.id,
        user_id: user?.id,
        comment: newComment,
        is_admin_reply: false
      });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setNewComment("");
      await fetchComments(selectedTicket.id);
    }
  };

  const articles = [
    {
      title: "Getting Started with Mollie Payments",
      category: "Payments",
      icon: CreditCard,
      description: "Complete guide to setting up Mollie payment processing",
      slug: "getting-started-with-mollie"
    },
    {
      title: "Getting Started with PTA Hub",
      category: "Getting Started",
      icon: BookOpen,
      description: "Learn the basics of setting up your PTA account",
      slug: "getting-started-with-pta-hub"
    },
    {
      title: "Managing Member Roles",
      category: "Administration",
      icon: FileText,
      description: "How to assign and manage member permissions",
      slug: "managing-member-roles"
    },
    {
      title: "Creating Your First Event",
      category: "Events",
      icon: Video,
      description: "Step-by-step guide to event planning",
      slug: "creating-your-first-event"
    },
    {
      title: "Setting Up Fundraising Goals",
      category: "Fundraising",
      icon: FileText,
      description: "Track and manage your fundraising objectives",
      slug: "setting-up-fundraising-goals"
    },
    {
      title: "Using the Raffle Game",
      category: "Games",
      icon: Video,
      description: "Complete guide to running digital raffles",
      slug: "using-the-raffle-game"
    },
    {
      title: "Financial Reporting Guide",
      category: "Finance",
      icon: FileText,
      description: "Generate and export financial reports",
      slug: "financial-reporting-guide"
    },
    ...newArticleTiles.map(t => ({
      title: t.title,
      category: t.category,
      icon: t.icon,
      description: t.description,
      slug: t.slug,
    })),
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'closed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pt-12 md:pt-16">
      <SEOHead slug="support" />
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">Support Centre</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Find answers to your questions or get in touch with our team
        </p>
      </div>

      {/* Search */}
      <Card className="shadow-card max-w-3xl mx-auto">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search for help articles, guides, and videos..."
              className="pl-10 h-12 text-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Support Tickets Section - Only for logged in members with active subscription */}
      {canSubmitTickets && (
        <Card className="shadow-card max-w-6xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-primary" />
                  My Support Tickets
                </CardTitle>
                <CardDescription>View and manage your support tickets</CardDescription>
              </div>
              <Button onClick={() => setTicketDialogOpen(true)}>
                <Ticket className="h-4 w-4 mr-2" />
                Submit Ticket
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {tickets.length > 0 ? (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => handleViewTicket(ticket)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium">{ticket.subject}</h4>
                        <Badge variant="outline" className="text-xs">
                          {ticket.category}
                        </Badge>
                        <Badge className={`${getStatusColor(ticket.status)} text-xs`}>
                          {ticket.status}
                        </Badge>
                        {ticket.has_pending_admin_reply && (
                          <Badge className="text-xs bg-emerald-500 hover:bg-emerald-500 text-white animate-pulse">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            New reply from support
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">{ticket.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Created {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTicket(ticket.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No tickets yet. Click "Submit Ticket" to create your first support ticket.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Help Articles */}
      {(() => {
        const CATEGORY_ALIASES: Record<string, string> = {
          "Finance": "Payments",
          "Administration": "Members & Roles",
          "Games": "Fundraising",
        };
        const CATEGORY_ORDER = [
          "Getting Started",
          "Subscriptions & Billing",
          "Payments",
          "Members & Roles",
          "Events",
          "Fundraising",
          "Dashboards",
        ];
        const normalised = articles.map(a => ({ ...a, category: CATEGORY_ALIASES[a.category] ?? a.category }));
        const q = searchQuery.trim().toLowerCase();
        const isSearching = q.length > 0;
        const matches = (a: typeof normalised[number]) =>
          a.title.toLowerCase().includes(q) || a.description.toLowerCase().includes(q);
        const filtered = isSearching ? normalised.filter(matches) : normalised;

        const grouped = CATEGORY_ORDER.map(cat => ({
          category: cat,
          items: filtered.filter(a => a.category === cat),
        })).filter(g => g.items.length > 0);

        const renderCard = (article: typeof normalised[number], i: number) => {
          const Icon = article.icon;
          const cardContent = (
            <Card className="shadow-card hover:shadow-elevated transition-shadow cursor-pointer h-full">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{article.title}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{article.description}</p>
                <Button variant="link" className="px-0 mt-2">Read more →</Button>
              </CardContent>
            </Card>
          );
          return article.slug ? (
            <Link key={i} to={`/support/${article.slug}`} className="block">{cardContent}</Link>
          ) : (
            <div key={i}>{cardContent}</div>
          );
        };

        return (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">
              {isSearching ? `Search Results (${filtered.length})` : "Help Articles"}
            </h2>

            {isSearching ? (
              filtered.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map(renderCard)}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No articles match "{searchQuery}".
                </p>
              )
            ) : (
              <Accordion type="multiple" defaultValue={["Getting Started"]} className="space-y-3">
                {grouped.map(group => (
                  <AccordionItem
                    key={group.category}
                    value={group.category}
                    className="border border-border rounded-lg px-4 bg-card"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <span className="flex items-center gap-3">
                        <span className="text-lg font-semibold">{group.category}</span>
                        <Badge variant="secondary">{group.items.length}</Badge>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2 pb-2">
                        {group.items.map(renderCard)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        );
      })()}

      {/* Video Tutorials */}
      <Card className="shadow-card max-w-6xl mx-auto mb-12">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            Video Tutorials
          </CardTitle>
          <CardDescription>Watch step-by-step guides</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="aspect-video bg-muted rounded mb-3 flex items-center justify-center">
                <Video className="h-8 w-8 text-muted-foreground" />
              </div>
              <h4 className="font-semibold mb-1">Quick Start Guide</h4>
              <p className="text-sm text-muted-foreground">5 min tutorial</p>
            </div>
            <div className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="aspect-video bg-muted rounded mb-3 flex items-center justify-center">
                <Video className="h-8 w-8 text-muted-foreground" />
              </div>
              <h4 className="font-semibold mb-1">Event Management</h4>
              <p className="text-sm text-muted-foreground">8 min tutorial</p>
            </div>
            <div className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="aspect-video bg-muted rounded mb-3 flex items-center justify-center">
                <Video className="h-8 w-8 text-muted-foreground" />
              </div>
              <h4 className="font-semibold mb-1">Financial Reports</h4>
              <p className="text-sm text-muted-foreground">6 min tutorial</p>
            </div>
            <a 
              href="https://youtu.be/Bgxkhmt4vLs" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer block"
            >
              <div className="aspect-video bg-muted rounded mb-3 overflow-hidden">
                <img 
                  src={new URL("@/assets/shop-video-thumbnail.jpg", import.meta.url).href}
                  alt="PTA Shop Tutorial"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold mb-1">PTA Shop</h4>
              <p className="text-sm text-muted-foreground">Tutorial video</p>
            </a>
            <a 
              href="https://youtu.be/3FGUHDQpP2k" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer block"
            >
              <div className="aspect-video bg-muted rounded mb-3 overflow-hidden">
                <img 
                  src={new URL("@/assets/library-video-thumbnail.jpg", import.meta.url).href}
                  alt="Library Tutorial"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold mb-1">Library</h4>
              <p className="text-sm text-muted-foreground">Tutorial video</p>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Submit Ticket Dialog */}
      <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Submit Support Ticket</DialogTitle>
            <DialogDescription>
              Provide details about your issue and our team will respond soon
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitTicket} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ticket-subject">Subject *</Label>
              <Input
                id="ticket-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of your issue"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticket-category">Category *</Label>
              <Select value={category} onValueChange={setCategory} required>
                <SelectTrigger id="ticket-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">Technical Support</SelectItem>
                  <SelectItem value="billing">Billing & Payments</SelectItem>
                  <SelectItem value="features">Feature Request</SelectItem>
                  <SelectItem value="account">Account Management</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticket-description">Description *</Label>
              <Textarea
                id="ticket-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please provide as much detail as possible..."
                rows={6}
                required
              />
            </div>

            {articleSuggestions.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Lightbulb className="h-4 w-4 text-primary" />
                  These articles might help
                </div>
                <div className="space-y-1">
                  {articleSuggestions.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/support/${s.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors group"
                    >
                      <ArrowRight className="h-3 w-3 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                      <span className="underline-offset-2 group-hover:underline">{s.title}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{s.category}</span>
                    </Link>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground pt-1">
                  Found your answer? You can close this dialog without submitting.
                </p>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setTicketDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Ticket"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View/Edit Ticket Dialog */}
      <Dialog open={viewTicketDialogOpen} onOpenChange={setViewTicketDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                {isEditingHeader ? (
                  <div className="space-y-2">
                    <Input
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      placeholder="Subject"
                      className="text-base font-semibold"
                    />
                  </div>
                ) : (
                  <DialogTitle className="flex items-center gap-2 flex-wrap">
                    <span className="break-words">{selectedTicket?.subject}</span>
                    <Badge className={getStatusColor(selectedTicket?.status)}>
                      {selectedTicket?.status}
                    </Badge>
                  </DialogTitle>
                )}
                <DialogDescription className="mt-1">
                  Ticket #{selectedTicket?.id?.slice(0, 8)} • Created{" "}
                  {selectedTicket?.created_at
                    ? new Date(selectedTicket.created_at).toLocaleDateString()
                    : ""}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-1 shrink-0 mr-6">
                {isEditingHeader ? (
                  <>
                    <Button size="icon" variant="ghost" onClick={handleSaveHeader} title="Save">
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setIsEditingHeader(false);
                        setEditSubject(selectedTicket?.subject || "");
                        setEditDescription(selectedTicket?.description || "");
                      }}
                      title="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditSubject(selectedTicket?.subject || "");
                        setEditDescription(selectedTicket?.description || "");
                        setIsEditingHeader(true);
                      }}
                      title="Edit title and description"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => selectedTicket && handleDeleteTicket(selectedTicket.id)}
                      title="Delete ticket"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Description block */}
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Description
              </Label>
              {isEditingHeader ? (
                <Textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={4}
                  className="mt-1.5"
                />
              ) : (
                <p className="mt-1.5 text-sm whitespace-pre-wrap text-foreground">
                  {selectedTicket?.description || (
                    <span className="text-muted-foreground italic">No description</span>
                  )}
                </p>
              )}
            </div>
          </DialogHeader>

          {/* Conversation */}
          <div className="flex-1 overflow-y-auto px-6 py-4 bg-muted/30">
            <h3 className="font-semibold flex items-center gap-2 mb-4 text-sm">
              <MessageSquare className="h-4 w-4" />
              Conversation ({comments.length})
            </h3>
            {comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No replies yet. Add a comment below to continue the conversation.
              </p>
            ) : (
              <div className="space-y-3">
                {comments.map((comment) => {
                  const isAdmin = comment.is_admin_reply;
                  return (
                    <div
                      key={comment.id}
                      className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-4 py-2.5 shadow-sm ${
                          isAdmin
                            ? "bg-background border border-border"
                            : "bg-primary text-primary-foreground"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-xs">
                            {isAdmin ? "Support Team" : "You"}
                          </span>
                          {isAdmin && (
                            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                              Admin
                            </Badge>
                          )}
                          <span
                            className={`text-[10px] ml-auto ${
                              isAdmin ? "text-muted-foreground" : "opacity-80"
                            }`}
                          >
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{comment.comment}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reply composer */}
          <div className="border-t px-6 py-4 shrink-0 bg-background">
            <Label htmlFor="new-comment" className="text-xs uppercase tracking-wide text-muted-foreground">
              Add a reply
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Textarea
                id="new-comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Type your message..."
                rows={2}
                className="flex-1 min-h-[60px]"
              />
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                className="self-end"
              >
                <Send className="h-4 w-4 mr-2" />
                Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}